import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import JsonLd from '@/components/feature/JsonLd';

const articleImages: Record<string, string> = {
  'art-001': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20geometric%20shapes%2C%20minimalist%20corporate%20style%2C%20professional%20technology%20platform%20theme%2C%20clean%20aesthetic%20with%20subtle%20textures%2C%20editorial%20quality&width=800&height=500&seq=article-platform-overview&orientation=landscape',
  'art-002': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20golden%20amber%20gradients%2C%20soft%20flowing%20shapes%2C%20minimalist%20user%20friendly%20style%2C%20professional%20registration%20portal%20theme%2C%20clean%20aesthetic%2C%20inviting%20atmosphere&width=800&height=500&seq=article-registration-guide&orientation=landscape',
  'art-003': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20geometric%20security%20shapes%2C%20minimalist%20login%20authentication%20theme%2C%20professional%20digital%20security%20style%2C%20clean%20and%20trustworthy%20aesthetic&width=800&height=500&seq=article-login-methods&orientation=landscape',
  'art-004': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20magnifying%20glass%20geometric%20shapes%2C%20minimalist%20search%20discovery%20theme%2C%20professional%20technology%20platform%20style%2C%20clean%20editorial%20quality&width=800&height=500&seq=article-search-guide&orientation=landscape',
  'art-005': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20golden%20cream%20gradients%2C%20soft%20data%20visualization%20shapes%2C%20minimalist%20analytics%20dashboard%20theme%2C%20professional%20technology%20platform%20style%2C%20clean%20corporate%20aesthetic&width=800&height=500&seq=article-data-center&orientation=landscape',
  'art-006': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20shield%20security%20geometric%20shapes%2C%20minimalist%20cybersecurity%20theme%2C%20professional%20protection%20concept%2C%20clean%20trustworthy%20aesthetic&width=800&height=500&seq=article-security&orientation=landscape',
  'art-007': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20golden%20amber%20gradients%2C%20soft%20speed%20performance%20geometric%20shapes%2C%20minimalist%20high%20performance%20theme%2C%20professional%20technology%20platform%20style%2C%20clean%20dynamic%20aesthetic&width=800&height=500&seq=article-performance&orientation=landscape',
  'art-008': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20mobile%20device%20geometric%20shapes%2C%20minimalist%20mobile%20technology%20theme%2C%20professional%20cross%20platform%20style%2C%20clean%20modern%20aesthetic&width=800&height=500&seq=article-mobile-guide&orientation=landscape',
  'art-009': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20golden%20amber%20gradients%2C%20soft%20settings%20geometric%20shapes%2C%20minimalist%20account%20management%20theme%2C%20professional%20user%20profile%20style%2C%20clean%20friendly%20aesthetic&width=800&height=500&seq=article-account-settings&orientation=landscape',
  'art-010': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20payment%20transaction%20geometric%20shapes%2C%20minimalist%20subscription%20management%20theme%2C%20professional%20fintech%20style%2C%20clean%20reliable%20aesthetic&width=800&height=500&seq=article-payment&orientation=landscape',
  'art-011': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20golden%20amber%20gradients%2C%20soft%20support%20service%20geometric%20shapes%2C%20minimalist%20customer%20care%20theme%2C%20professional%20help%20desk%20style%2C%20clean%20supportive%20aesthetic&width=800&height=500&seq=article-customer-service&orientation=landscape',
  'art-012': 'https://readdy.ai/api/search-image?query=Modern%20abstract%20illustration%20with%20warm%20amber%20cream%20gradients%2C%20soft%20update%20version%20geometric%20shapes%2C%20minimalist%20changelog%20documentation%20theme%2C%20professional%20software%20release%20style%2C%20clean%20organized%20aesthetic&width=800&height=500&seq=article-changelog&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const currentSlug = slug || 'platform-features';
  const category = getCategoryBySlug(currentSlug);
  const categoryArticles = getArticlesByCategory(currentSlug);
  const totalPages = Math.ceil(categoryArticles.length / pageSize);
  const displayedArticles = categoryArticles.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentPage(1);
  }, [currentSlug]);

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
  };

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-foreground-500">分类不存在</p>
      </div>
    );
  }

  const categoryUrl = `https://qiaojzx.cn/category/${category.slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qiaojzx.cn' },
      { '@type': 'ListItem', position: 2, name: category.name, item: categoryUrl },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - qiaojzx.cn`,
    description: category.description,
    url: categoryUrl,
    isPartOf: { '@type': 'WebSite', name: 'qiaojzx.cn', url: 'https://qiaojzx.cn' },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SEOMeta
        title={`${category.name} - qiaojzx.cn 官方平台介绍与使用指南`}
        description={category.description}
        keywords={`qiaojzx.cn,${category.name},qiaojzx.cn 官网入口,qiaojzx.cn 注册登录,qiaojzx.cn 使用教程`}
        canonicalUrl={categoryUrl}
      />
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={webpageSchema} />
      <main className="pt-20 pb-16 md:pb-24">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: category.name }]} />

          <div className="mb-10">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              {category.name}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">{category.description}</p>
          </div>

          <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  cat.slug === currentSlug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:bg-background-100'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayedArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group flex flex-col bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300 overflow-hidden"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={articleImages[article.id]}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-foreground-400 text-xs">{article.readTime}</span>
                    <span className="text-foreground-400 text-xs">{article.publishDate}</span>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm md:text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-foreground-500 text-xs leading-relaxed line-clamp-2 flex-1">
                    {article.summary}
                  </p>
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200/70">
                    <div className="flex items-center gap-2 flex-wrap">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="flex items-center gap-1 text-primary-600 text-xs font-medium whitespace-nowrap">
                      阅读全文 <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 rounded-lg border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                    p === currentPage
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-200/70 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 rounded-lg border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          )}
        </div>
      </main>
    </>
  );
}